import { PublicClientApplication } from '@azure/msal-browser';
import { Capacitor } from '@capacitor/core';
import { MsAuthPlugin } from '@recognizebv/capacitor-plugin-msauth';

const MS_CLIENT_ID = import.meta.env.VITE_MS_CLIENT_ID;
const MS_TENANT = import.meta.env.VITE_MS_TENANT || 'common';

// Web MSAL.js scopes (include offline_access for refresh tokens).
const MS_SCOPES_WEB = [
  'Calendars.ReadWrite',
  'Contacts.ReadWrite',
  'User.Read',
  'offline_access',
];

// Native plugin scopes — MSAL iOS SDK auto-adds openid/profile/offline_access.
// Including them here is an error per the plugin docs.
const MS_SCOPES_NATIVE = [
  'Calendars.ReadWrite',
  'Contacts.ReadWrite',
  'User.Read',
];

const isNative = () => Capacitor?.isNativePlatform?.() === true;

// In-memory cache of the native sign-in state (mirrored to localStorage so it
// survives reloads inside the same install).
let nativeAccount = null;
try {
  const stored = localStorage.getItem('sr:ms:account');
  if (stored) nativeAccount = JSON.parse(stored);
} catch {}

function setNativeAccount(acc) {
  nativeAccount = acc;
  try {
    if (acc) localStorage.setItem('sr:ms:account', JSON.stringify(acc));
    else     localStorage.removeItem('sr:ms:account');
  } catch {}
}

function decodeJwtPayload(jwt) {
  try {
    const payload = jwt.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch { return {}; }
}

// ──────────────────────────────────────────────────────────────────────────
// Web (MSAL.js) — only used in the browser. Same flow as before.
// ──────────────────────────────────────────────────────────────────────────
let msalInstance = null;
function getMsalWeb() {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: MS_CLIENT_ID,
        authority: `https://login.microsoftonline.com/${MS_TENANT}`,
        redirectUri: window.location.origin,
      },
      cache: { cacheLocation: 'localStorage' },
    });
  }
  return msalInstance;
}

// ──────────────────────────────────────────────────────────────────────────
// Native (Capacitor MSAL plugin) — uses native MSAL iOS SDK under the hood,
// opens system Safari for sign-in (not embedded WebView).
// ──────────────────────────────────────────────────────────────────────────
async function nativeLogin() {
  const result = await MsAuthPlugin.login({
    clientId: MS_CLIENT_ID,
    tenant: MS_TENANT,
    scopes: MS_SCOPES_NATIVE,
  });
  // Extract username from the id token so we can show it in the UI.
  const claims = decodeJwtPayload(result.idToken);
  const username = claims.preferred_username || claims.upn || claims.email || '';
  const acc = { username, accessToken: result.accessToken };
  setNativeAccount(acc);
  return acc;
}

async function nativeLogout() {
  try {
    await MsAuthPlugin.logout({ clientId: MS_CLIENT_ID, tenant: MS_TENANT });
  } catch {}
  setNativeAccount(null);
}

// ──────────────────────────────────────────────────────────────────────────
// Unified API — same shape regardless of platform.
// ──────────────────────────────────────────────────────────────────────────
export const ms = {
  getAccount() {
    if (isNative()) return nativeAccount;
    try { return getMsalWeb().getAllAccounts()[0] || null; } catch { return null; }
  },

  async signIn() {
    if (isNative()) return nativeLogin();
    const msal = getMsalWeb();
    await msal.initialize?.();
    const result = await msal.loginPopup({ scopes: MS_SCOPES_WEB });
    return result.account;
  },

  async signOut() {
    if (isNative()) return nativeLogout();
    try {
      const msal = getMsalWeb();
      await msal.initialize?.();
      await msal.clearCache?.();
    } catch {}
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('msal.') || k.includes('login.microsoftonline'))
        .forEach((k) => localStorage.removeItem(k));
    } catch {}
    msalInstance = null;
  },

  async getAccessToken() {
    if (isNative()) {
      // The native plugin returns a fresh token each login() call (MSAL iOS
      // SDK handles silent refresh under the hood). Just call login() again —
      // user won't see a UI prompt if the cached refresh token is valid.
      const result = await MsAuthPlugin.login({
        clientId: MS_CLIENT_ID,
        tenant: MS_TENANT,
        scopes: MS_SCOPES_NATIVE,
      });
      if (nativeAccount) {
        setNativeAccount({ ...nativeAccount, accessToken: result.accessToken });
      }
      return result.accessToken;
    }
    const msal = getMsalWeb();
    await msal.initialize?.();
    const account = this.getAccount();
    if (!account) throw new Error('Not signed in to Microsoft.');
    try {
      const result = await msal.acquireTokenSilent({ scopes: MS_SCOPES_WEB, account });
      return result.accessToken;
    } catch {
      const result = await msal.acquireTokenPopup({ scopes: MS_SCOPES_WEB, account });
      return result.accessToken;
    }
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Admin consent detection + URL generation
// ────────────────────────────────────────────────────────────────────────────

/**
 * Detect Azure AD errors that mean the user can't self-consent and admin
 * approval is required for their tenant.
 */
export function isAdminConsentRequiredError(err) {
  if (!err) return false;
  const code = String(err.errorCode || '').toLowerCase();
  const msg = String(err.errorMessage || err.message || '');
  return (
    code === 'user_admin_consent_required' ||
    code === 'admin_consent_required' ||
    code === 'consent_required' ||
    msg.includes('AADSTS65001') ||  // user/admin has not consented
    msg.includes('AADSTS530021') || // admin approval required
    msg.includes('AADSTS900971') || // admin needs to consent
    msg.includes('admin_consent_required') ||
    msg.includes('AdminConsentRequired')
  );
}

/**
 * Generate a one-click URL an IT admin can use to approve ScanRemind for their
 * entire tenant. Using 'common' so the admin signs into their own tenant when
 * they click it — no need to know their tenant ID up front.
 */
export function getAdminConsentUrl() {
  const redirectUri = encodeURIComponent(window.location.origin);
  return `https://login.microsoftonline.com/common/adminconsent` +
    `?client_id=${MS_CLIENT_ID}` +
    `&redirect_uri=${redirectUri}` +
    `&scope=${encodeURIComponent(MS_SCOPES_WEB.join(' '))}`;
}

// ────────────────────────────────────────────────────────────────────────────
// Microsoft Graph writers (Outlook calendar + contacts)
// ────────────────────────────────────────────────────────────────────────────

async function graphPost(path, body) {
  const token = await ms.getAccessToken();
  const res = await fetch(`https://graph.microsoft.com/v1.0${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Graph API error: ${res.status} ${text}`);
  }
  return res.json();
}

export async function addToCalendar({ category, fields }) {
  if (!ms.getAccount()) {
    throw new Error('Please connect Microsoft 365 in Settings first.');
  }
  return graphPost('/me/events', buildEvent({ category, fields }));
}

export async function addContact({ fields }) {
  if (!ms.getAccount()) {
    throw new Error('Please connect Microsoft 365 in Settings first.');
  }
  return graphPost('/me/contacts', buildContact(fields));
}

function buildEvent({ category, fields }) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (category === 'bill') {
    const date = fields.due_date;
    // Microsoft Graph rule: all-day events must have end = NEXT day's midnight,
    // not 23:59:59 of the same day. Rejects with ErrorInvalidRequest otherwise.
    const next = new Date(`${date}T00:00:00`);
    next.setDate(next.getDate() + 1);
    const pad = (n) => String(n).padStart(2, '0');
    const nextDate = `${next.getFullYear()}-${pad(next.getMonth()+1)}-${pad(next.getDate())}`;
    return {
      subject: `Bill due: ${fields.payee || 'Unknown'}${fields.amount ? ` — $${fields.amount}` : ''}`,
      body: { contentType: 'Text', content: fields.notes || '' },
      start: { dateTime: `${date}T00:00:00.0000000`,     timeZone: tz },
      end:   { dateTime: `${nextDate}T00:00:00.0000000`, timeZone: tz },
      isAllDay: true,
      reminderMinutesBeforeStart: 60 * 24,
      categories: ['Bill'],
    };
  }

  const startIso = fields.datetime;
  const durationMin = Number(fields.duration_minutes) || (category === 'flyer' ? 120 : 30);
  const start = new Date(startIso);
  const end = new Date(start.getTime() + durationMin * 60 * 1000);

  return {
    subject: fields.title || (category === 'appointment' ? fields.provider : 'Event'),
    body: { contentType: 'Text', content: fields.notes || fields.description || '' },
    location: fields.location ? { displayName: fields.location } : undefined,
    start: { dateTime: toLocalIso(start), timeZone: tz },
    end:   { dateTime: toLocalIso(end),   timeZone: tz },
    categories: [category.charAt(0).toUpperCase() + category.slice(1)],
  };
}

function buildContact(f) {
  const contact = {
    givenName:    f.given_name || '',
    surname:      f.surname || '',
    companyName:  f.company || '',
    jobTitle:     f.job_title || '',
    personalNotes: f.notes || '',
  };
  if (f.email) {
    contact.emailAddresses = [{ address: f.email, name: `${f.given_name || ''} ${f.surname || ''}`.trim() }];
  }
  if (f.business_phone) contact.businessPhones = [f.business_phone];
  if (f.mobile_phone)   contact.mobilePhone = f.mobile_phone;
  if (f.website)        contact.businessHomePage = f.website;
  if (f.address)        contact.businessAddress = { street: f.address };
  return contact;
}

// ────────────────────────────────────────────────────────────────────────────
// Universal exports: .ics (calendar) and .vcf (contacts)
// Works on Apple Calendar/Contacts, Google Calendar/Contacts, Outlook desktop,
// Yahoo, etc. Browser downloads the file; user taps it; OS opens the right app.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Generate and trigger a download of an .ics file for the given event.
 * Universal calendar format (RFC 5545).
 */
export function downloadIcs({ category, fields }) {
  const ics = buildIcs({ category, fields });
  const title = sanitizeFilename(
    fields.title || fields.payee || fields.provider || 'event'
  );
  triggerDownload(ics, `${title}.ics`, 'text/calendar');
}

/**
 * Generate and trigger a download of a .vcf file for the contact.
 * Universal contact format (RFC 6350).
 */
export function downloadVcf({ fields }) {
  const vcf = buildVcf(fields);
  const name = sanitizeFilename(
    [fields.given_name, fields.surname].filter(Boolean).join(' ') || 'contact'
  );
  triggerDownload(vcf, `${name}.vcf`, 'text/vcard');
}

function buildIcs({ category, fields }) {
  const now = formatIcsUtc(new Date());
  const uid = `${crypto.randomUUID()}@scanremind.com`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ScanRemind//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
  ];

  if (category === 'bill') {
    const date = (fields.due_date || '').replaceAll('-', '');
    const next = formatIcsDateAdd(fields.due_date, 1);
    lines.push(
      `DTSTART;VALUE=DATE:${date}`,
      `DTEND;VALUE=DATE:${next}`,
      `SUMMARY:${escIcs(`Bill due: ${fields.payee || 'Unknown'}${fields.amount ? ` — $${fields.amount}` : ''}`)}`,
      'BEGIN:VALARM',
      'TRIGGER:-P1D',
      'ACTION:DISPLAY',
      `DESCRIPTION:${escIcs(`Pay ${fields.payee || ''}`)}`,
      'END:VALARM',
    );
  } else {
    const start = new Date(fields.datetime);
    const durationMin = Number(fields.duration_minutes) || (category === 'flyer' ? 120 : 30);
    const end = new Date(start.getTime() + durationMin * 60 * 1000);
    lines.push(
      `DTSTART:${formatIcsLocal(start)}`,
      `DTEND:${formatIcsLocal(end)}`,
      `SUMMARY:${escIcs(fields.title || fields.provider || 'Event')}`,
    );
    if (fields.location)    lines.push(`LOCATION:${escIcs(fields.location)}`);
    const desc = fields.notes || fields.description;
    if (desc)               lines.push(`DESCRIPTION:${escIcs(desc)}`);
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n');
}

function buildVcf(f) {
  const fullName = [f.given_name, f.surname].filter(Boolean).join(' ') || 'Contact';
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${escVcf(fullName)}`,
    `N:${escVcf(f.surname || '')};${escVcf(f.given_name || '')};;;`,
  ];
  if (f.company)        lines.push(`ORG:${escVcf(f.company)}`);
  if (f.job_title)      lines.push(`TITLE:${escVcf(f.job_title)}`);
  if (f.email)          lines.push(`EMAIL;TYPE=INTERNET:${escVcf(f.email)}`);
  if (f.mobile_phone)   lines.push(`TEL;TYPE=CELL:${escVcf(f.mobile_phone)}`);
  if (f.business_phone) lines.push(`TEL;TYPE=WORK:${escVcf(f.business_phone)}`);
  if (f.website)        lines.push(`URL:${escVcf(f.website)}`);
  if (f.address)        lines.push(`ADR;TYPE=WORK:;;${escVcf(f.address)};;;;`);
  if (f.notes)          lines.push(`NOTE:${escVcf(f.notes)}`);
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

// ─── helpers ────────────────────────────────────────────────────────────────

function escIcs(s) {
  return String(s).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}

function escVcf(s) {
  return String(s).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}

function formatIcsUtc(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth()+1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

function formatIcsLocal(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
}

function formatIcsDateAdd(yyyyMmDd, days) {
  const d = new Date(`${yyyyMmDd}T00:00:00`);
  d.setDate(d.getDate() + days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}`;
}

function sanitizeFilename(s) {
  return String(s).replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 60);
}

function triggerDownload(text, filename, mime) {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function toLocalIso(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}
